import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiAlertCircle, FiCheckCircle, FiTrendingUp, FiMapPin, FiCalendar } from 'react-icons/fi';
import { EVENT_INFO, TIMELINE_DATA } from '../data/mockData';

export default function CommandCenter() {
  const activeEvent = TIMELINE_DATA.find(t => t.status === 'active');
  const nextEvent = TIMELINE_DATA.find(t => t.status === 'pending');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-24 h-full overflow-y-auto pr-2 no-scrollbar">
      {/* Header Status Bar */}
      <div className="glass-panel p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative overflow-hidden border-white/5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#9D4EDD]/20 to-transparent blur-[100px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> {EVENT_INFO.status}
            </span>
            <span className="text-[#94A3B8] text-[10px] font-black tracking-widest uppercase opacity-50">{EVENT_INFO.id}</span>
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight italic mb-2">
            {EVENT_INFO.name.split(' ').map((word, i) => i === 2 ? <span key={i} className="text-[#9D4EDD]">{word} </span> : word + ' ')}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-[#94A3B8] text-sm">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiMapPin} className="text-[#E5C158]" />
              <span className="font-bold">{EVENT_INFO.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiCalendar} className="text-[#9D4EDD]" />
              <span className="font-mono">{EVENT_INFO.date}</span>
            </div>
          </div>
        </div>

        <div className="relative z-10 glass-panel bg-black/60 px-8 py-6 rounded-2xl border-white/10 text-center min-w-[240px] shadow-2xl">
          <p className="text-[10px] text-[#E5C158] uppercase tracking-[0.4em] mb-2 font-black">Transmission Window</p>
          <p className="text-3xl font-mono text-white text-glow-gold tracking-tighter">{EVENT_INFO.countdown}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-panel p-8 lg:col-span-2 flex flex-col border-white/5 h-full min-h-[400px]">
          <h2 className="text-lg font-black text-white mb-6 flex items-center gap-3 uppercase tracking-wider">
            <SafeIcon icon={FiActivity} className="text-[#9D4EDD]" /> 
            Live Execution Stream
          </h2>
          <div className="flex-1 space-y-4">
            {activeEvent && (
              <div className="p-6 rounded-2xl bg-gradient-to-r from-[#9D4EDD]/10 via-[#9D4EDD]/5 to-transparent border border-[#9D4EDD]/20 relative overflow-hidden group">
                <div className="absolute left-0 top-0 w-1.5 h-full bg-[#9D4EDD] shadow-[0_0_20px_#9D4EDD]"></div>
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-black text-[#9D4EDD] uppercase tracking-[0.3em] bg-[#9D4EDD]/10 px-3 py-1.5 rounded-lg border border-[#9D4EDD]/20">Active Node</span>
                  <span className="text-sm font-mono text-white bg-black/40 px-3 py-1 rounded-lg border border-white/5">{activeEvent.time}</span>
                </div>
                <h3 className="text-2xl font-black text-white mb-2 italic">{activeEvent.title}</h3>
                <p className="text-sm text-[#94A3B8] font-bold">Protocol Owner: <span className="text-white">{activeEvent.owner}</span></p>
              </div>
            )}
            {nextEvent && (
              <div className="p-6 rounded-2xl bg-white/5 border border-white/5 opacity-60 hover:opacity-100 transition-opacity">
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-black text-[#94A3B8] uppercase tracking-[0.3em]">Queued Node</span>
                  <span className="text-sm font-mono text-[#94A3B8]">{nextEvent.time}</span>
                </div>
                <h3 className="text-xl font-bold text-white/80 mb-2">{nextEvent.title}</h3>
                <p className="text-sm text-[#94A3B8]">Protocol Owner: {nextEvent.owner}</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-panel p-8 relative overflow-hidden group border-white/5">
            <div className="absolute inset-0 bg-gradient-to-b from-[#E5C158]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <h2 className="text-[10px] font-black text-[#94A3B8] uppercase tracking-[0.4em] mb-6">Guest Energy Vitals</h2>
            <div className="flex items-center justify-center relative py-6">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle cx="80" cy="80" r="72" stroke="rgba(255,255,255,0.05)" strokeWidth="10" fill="none" />
                <circle cx="80" cy="80" r="72" stroke="#E5C158" strokeWidth="10" fill="none" strokeDasharray="452.4" strokeDashoffset={452.4 - (452.4 * EVENT_INFO.overallEnergy) / 100} className="drop-shadow-[0_0_15px_rgba(229,193,88,0.4)] transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-black text-white text-glow-gold tracking-tighter">{EVENT_INFO.overallEnergy}%</span>
                <span className="text-[10px] text-[#E5C158] uppercase tracking-[0.3em] mt-1 font-black">Optimal</span>
              </div>
            </div>
          </div>

          <div className="glass-panel p-8 border-white/5">
            <h2 className="text-[10px] font-black text-[#94A3B8] uppercase tracking-[0.4em] mb-6">System Telemetry</h2>
            <ul className="space-y-5">
              {[
                { icon: FiAlertCircle, color: "text-[#E5C158]", text: "VIP transport delay in Sector 4." },
                { icon: FiCheckCircle, color: "text-green-400", text: "Hologram nodes fully calibrated." },
                { icon: FiTrendingUp, color: "text-[#9D4EDD]", text: "Resource burn rate +12% vs projection." }
              ].map((alert, i) => (
                <li key={i} className="flex gap-4 items-start text-sm group">
                  <div className={`mt-0.5 shrink-0 ${alert.color}`}>
                    <SafeIcon icon={alert.icon} />
                  </div>
                  <span className="text-white/80 font-bold group-hover:text-white transition-colors">{alert.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
}